import { View } from "@-/com.npmjs.react-native";
import Svg, { G, Path } from "@-/com.npmjs.react-native-svg";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import {
  LEARN_CARD_BINDER_TAB_FLARE_RADIUS,
  LEARN_CARD_BINDER_TAB_HEIGHT,
} from "#learn-card-shell-metrics";

const TOP_RADIUS = 2;
const FLARE = LEARN_CARD_BINDER_TAB_FLARE_RADIUS;
const HEIGHT = LEARN_CARD_BINDER_TAB_HEIGHT;
const BASELINE_Y = HEIGHT - 0.5;

function colorFromChannels(channels: string | number | undefined): string {
  if (channels === undefined || channels === "") {
    return "transparent";
  }
  const value = String(channels);
  return value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")
    ? value
    : `hsl(${value})`;
}

/** Contiguous tab shoulders flare over their neighbours, rather than reserving a gap. */
export function LearnCardBinderTabStrip({
  activeIndex,
  extendRight = false,
  frontmostIndex = activeIndex,
  tabWidths,
}: {
  activeIndex: number;
  /** A hosted study strip's last reading also flares over the Details tab. */
  extendRight?: boolean;
  /** Details remains behind Learn even when its view is selected. */
  frontmostIndex?: number;
  tabWidths: readonly number[];
}) {
  const borderColor = colorFromChannels(useCSSVariable("--color-border"));
  const surfaceColor = colorFromChannels(useCSSVariable("--color-surface"));
  const mutedColor = colorFromChannels(useCSSVariable("--color-muted"));
  const width = tabWidths.reduce((total, tabWidth) => total + tabWidth, 0);
  const tabs = tabWidths.map((tabWidth, index) => {
    const x0 = tabWidths.slice(0, index).reduce((total, previousWidth) => total + previousWidth, 0);
    // Neighbours share one outline centerline, so there is no double-width divider.
    const left = x0 + (index === 0 ? 0.5 : -0.5);
    const right = x0 + tabWidth - 0.5;
    const hasLeftNeighbour = index > 0;
    const hasRightNeighbour = index < tabWidths.length - 1 || extendRight;
    const start = left - (hasLeftNeighbour ? FLARE : 0);
    const end = right + (hasRightNeighbour ? FLARE : 0);
    const outline = [
      `M ${start} ${BASELINE_Y}`,
      ...(hasLeftNeighbour ? [`A ${FLARE} ${FLARE} 0 0 0 ${left} ${BASELINE_Y - FLARE}`] : []),
      `L ${left} ${0.5 + TOP_RADIUS}`,
      `A ${TOP_RADIUS} ${TOP_RADIUS} 0 0 1 ${left + TOP_RADIUS} 0.5`,
      `L ${right - TOP_RADIUS} 0.5`,
      `A ${TOP_RADIUS} ${TOP_RADIUS} 0 0 1 ${right} ${0.5 + TOP_RADIUS}`,
      `L ${right} ${BASELINE_Y - (hasRightNeighbour ? FLARE : 0)}`,
      ...(hasRightNeighbour ? [`A ${FLARE} ${FLARE} 0 0 0 ${end} ${BASELINE_Y}`] : []),
    ].join(" ");
    return { end, index, outline, start };
  });
  // Rightmost tabs sit furthest back; the focused study section is drawn last.
  const layers = [
    ...tabs.filter((tab) => tab.index !== frontmostIndex).reverse(),
    ...tabs.filter((tab) => tab.index === frontmostIndex),
  ];

  return (
    <View className="absolute inset-0" pointerEvents="none">
      <Svg height={HEIGHT} width={width + (extendRight ? FLARE : 0)}>
        {layers.map(({ end, index, outline, start }) => {
          const isActive = index === activeIndex;
          return (
            <G key={index}>
              <Path
                d={
                  isActive ? `${outline} L ${end} ${HEIGHT} L ${start} ${HEIGHT} Z` : `${outline} Z`
                }
                fill={isActive ? surfaceColor : mutedColor}
              />
              <Path
                d={isActive ? outline : `${outline} Z`}
                fill="none"
                stroke={borderColor}
                strokeWidth={1}
              />
            </G>
          );
        })}
      </Svg>
    </View>
  );
}
